import { fabric } from 'fabric';
import { isUndefined, orderBy } from 'lodash';
import { useCallback } from 'react';
import Vec2 from 'vec2';

import {
    CANVAS_OBJECT_TYPE_PREFIX,
    CANVAS_OBJECT_TYPE_SUFFIX,
    DEFAULT_BUMPOUT_DEPTH,
    DEFAULT_DEPTH,
    DEFAULT_ZOOM,
    MINIMUM_GAP,
    MINIMUM_ZOOM,
    QUARTERPART_NAME,
    TEN_THOUSAND,
    ZOOM_MARGIN_ADJUSTMENT,
    ZOOM_PCT_CHANGE,
} from '@common/constants';
import { LineDirection, containsBumpOut, containsRadiusOut } from '@common/enums';
import { DIRECTION } from '@common/enums/drawing-types.enum';
import { BoundingBox, MathFunctions, SimpleLine, roundToArrowLengthGranularity } from '@common/helpers';
import { ArrowInfo, CornerInfo, DrawingEdge, DrawingShape, LineSegment, Skeleton, SkeletonSeg } from '@common/models';

import {
    COORDS_EPSILON,
    FREEDRAWER_REALTIMELENGTH_OFFSET,
    NORMALIZE_ARROW,
    OFFSET_HIGHLIGHT_BORDER,
    OFFSET_MEASUREMENT,
    OFFSET_MEASUREMENT_TEXT,
} from './canvas-settings';

/**
 * return the Vec2(left, top) point for the placement of a new shape in the canvas
 */
export const autoPlaceLocation = (canvas: fabric.Canvas): Vec2 => {
    if (!canvas || !canvas.getObjects()) {
        return new Vec2(0, 0);
    }
    const boundingBox = getCanvasBoundingBox(canvas);
    if (!boundingBox) {
        return new Vec2(0, 0);
    }
    if (Math.abs(boundingBox.left - boundingBox.right) > Math.abs(boundingBox.top - boundingBox.bottom)) {
        return new Vec2(boundingBox.left, boundingBox.bottom + 30);
    }
    return new Vec2(boundingBox.right + 30, boundingBox.top);
};

/**
 * autoPlaceTranslation given a source polygon, find a translation vector that will place it in a non-colliding location
 * @param sourcePoly: an array of points defining the polygon to be translated
 * @param polyArray: an array of polygons (each an array of points) to test for collisions and attempt to avoid
 * @returns Vec2: a vector defining how much to translate the sourcePoly to avoid collisions
 */
export const autoPlaceTranslation = (sourcePoly: Vec2[], polyArray: Vec2[][], ignoreIndex?: number): Vec2 => {
    // copy the original polygon so we can translate it if necessary
    let testPoly = sourcePoly.map((value) => {
        return value;
    });
    let result = new Vec2(0, 0);
    let collisions = MathFunctions.polygonCollisions(testPoly, polyArray);

    if (!collisions.some((x) => x) && MathFunctions.minDistanceToOtherPolys(testPoly, polyArray) > MINIMUM_GAP) {
        return result;
    }

    // bounding boxes for all polygons in the array
    const polyBounds = polyArray.map((value) => {
        return MathFunctions.findBoundingBox(value);
    });
    // and their center points
    const polyCenters = polyBounds.map((value) => {
        return value.getAbsoluteCenter() || new Vec2(0, 0);
    });

    // find the original dirVector which is the sum of the vectors between overlapping polygon centers
    // and the source center
    const sourceBounds = MathFunctions.findBoundingBox(sourcePoly) || new BoundingBox();
    const sourceCenter = sourceBounds.getAbsoluteCenter();
    let dirVector = new Vec2(0, 0);
    polyCenters.forEach((bCenter, index) => {
        if (collisions[index]) {
            const aVec = MathFunctions.getVectorBetweenPoints(sourceCenter, bCenter);
            dirVector.add(aVec.normalize());
        }
    });

    // if they're directly on top of each other, shift (right, down)
    if (dirVector.length() === 0) {
        dirVector = new Vec2(1, 1);
    }

    const shiftDistance = 10; // should be calculated to minimize attempts
    const scaledDirVector = dirVector.normalize(true).multiply(shiftDistance);

    // console.debug('initial collisions', collisions);
    let attempts = 50;
    while (
        (attempts-- && collisions.some((x) => x)) ||
        MathFunctions.minDistanceToOtherPolys(testPoly, polyArray) < MINIMUM_GAP
    ) {
        testPoly = testPoly.map((point) => point.subtract(scaledDirVector, true));
        collisions = MathFunctions.polygonCollisions(testPoly, polyArray, ignoreIndex);
        // console.debug('loop', attempts, testPoly);
        // console.debug('loop collisions', collisions);
    }

    // final result is the delta from any two common points of the source polygon and our translated test
    result = MathFunctions.getVectorBetweenPoints(testPoly[0], sourcePoly[0]);
    return result;
};

export const getUpperLeftLowerRight = (o: fabric.Object): [{ x: number; y: number }, { x: number; y: number }] => {
    const b = o.getBoundingRect(true, true);
    return [
        { x: b.left, y: b.top },
        { x: b.left + b.width, y: b.top + b.height },
    ];
};

export const getCanvasBoundingBox = (canvas: fabric.Canvas) => {
    const canvasPoints: Array<any> = canvas
        .getObjects()
        .map((o: fabric.Object) => {
            if (o.name?.includes(CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP)) {
                const groupCenterPoint = o.getCenterPoint();
                const groupCenterVec2 = new Vec2(groupCenterPoint.x, groupCenterPoint.y);
                const objectsInGroup = (o as fabric.Group).getObjects();
                const groupItems: any[] = [];
                objectsInGroup.forEach((child: any) => {
                    if (!child.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_ARC)) {
                        const points = getUpperLeftLowerRight(child);
                        const centerAdjustedPoints = [
                            new Vec2(points[0].x, points[0].y).add(groupCenterVec2, true),
                            new Vec2(points[1].x, points[1].y).add(groupCenterVec2, true),
                        ];
                        groupItems.push([
                            { x: centerAdjustedPoints[0].x, y: centerAdjustedPoints[0].y },
                            { x: centerAdjustedPoints[1].x, y: centerAdjustedPoints[1].y },
                        ]);
                    }
                });
                return groupItems;
            } else {
                return getUpperLeftLowerRight(o);
            }
        })
        .flat(2);
    return MathFunctions.findBoundingBox(canvasPoints);
};

/**
 * Zooms and/or pans the canvas to fit all objects.
 * Zooms to the scale to fit the image in the smallest dimension.  Pans to the center of all objects on the canvas
 * @param canvas - the canvas to auto zoom or pan
 * @param zoom - whether to zoom to fit all the elements
 * @param center - whether to center the canvas on the center of all the elements in the canvas
 */
export const autoZoomPanToFit = (canvas: fabric.Canvas, zoom: boolean, center: boolean): void => {
    if (isUndefined(canvas) || !canvas || !canvas.getObjects() || (!zoom && !center)) {
        return;
    }
    const boundingBox = getCanvasBoundingBox(canvas);
    if (!boundingBox) {
        return;
    }
    const bWidth = boundingBox.getWidth();
    const bHeight = boundingBox.getHeight();
    if (zoom && bWidth && bHeight) {
        const xRatio = bWidth / canvas.getWidth() / ZOOM_MARGIN_ADJUSTMENT;
        const yRatio = bHeight / canvas.getHeight() / ZOOM_MARGIN_ADJUSTMENT;
        const zoomRatio = Math.max(xRatio, yRatio);
        const zoomLevel = Math.max(MINIMUM_ZOOM, 1 / zoomRatio);
        canvas.setZoom(zoomLevel);
    }
    if (center) {
        const currentZoom = canvas.getZoom();
        canvas.absolutePan({
            x: boundingBox.left * currentZoom - (canvas.getWidth() - bWidth * currentZoom) / 2,
            y: boundingBox.top * currentZoom - (canvas.getHeight() - bHeight * currentZoom) / 2,
        });
    }
};

/**
 * creates a defined array of steps for zooming based around a mid-point value and stepping up/down by a defined %
 * the array will extend the same number of steps on either side of the middle until the min value is reached
 * Invalid values for min, max, pctChange are sanitized to something mathematically useful
 * NOTE: this used to make the zoom levels deterministic
 * @param min - minimum zoom value (0 < min < middle)
 * @param middle - the midpoint zoom value for the array (0 < min < middle)
 * @param pctChange - how much each step should change (0.1=10%) value (0 < pctChange < 1)
 */
export const makeZoomStepArray = (min: number, middle: number, pctChange: number): Array<number> => {
    const adjMiddle = Math.abs(middle) > 0 ? Math.abs(middle) : 1; // fallback to 1 because, whatever - this should probably error
    const adjPct = pctChange > 0 && pctChange < 1 ? pctChange : 0.2; // default 20%
    const adjMin = min > 0 && min < adjMiddle ? min : adjPct / 2; // fallback is half the % change

    const steps = new Array(1).fill(adjMiddle);
    for (let i = 0; steps[0] > adjMin; i++) {
        steps.splice(0, 0, steps[0] * (1 - adjPct));
        steps.push(steps[steps.length - 1] * (1 + adjPct));
    }
    return steps;
};

/**
 * Zoom the canvas IN based on the zoomByStep function
 * TODO does not center
 * @param canvas the canvas to zoom
 * @param center recenter the canvas to this point
 */
export const zoomIn = (canvas: fabric.Canvas, center?: Vec2) => {
    if (!canvas) {
        return;
    }
    if (center) {
        canvas.absolutePan(center);
    }
    canvas.setZoom(zoomByStep(canvas, 1));
    autoZoomPanToFit(canvas, false, true);
};

/**
 * Zoom the canvas OUT based on the zoomByStep function
 * TODO these should probably include a Point parameter too that centers the zoom on that point with absolutePan
 * @param canvas the canvas to zoom
 * @param center recenter the canvas to this point
 */
export const zoomOut = (canvas: fabric.Canvas, center?: Vec2) => {
    if (!canvas) {
        return;
    }
    if (center) {
        canvas.absolutePan(center);
    }
    canvas.setZoom(zoomByStep(canvas, -1));
    autoZoomPanToFit(canvas, false, true);
};

/**
 * returns the value to set the zoom (it does not actually zoom the canvas!!!!) to zoom in or out from the current level
 * with "zoom to fit" as an option, the current zoom level may not match one of our defined steps, but this will step to the
 * next closest value
 * NOTE: This relies on a ZOOM_STEPS array (which gets generated every time right now) that includes all the valid steps
 * NOTE: there's a "straight math" version of this commented out that works fine, except it drifts from any defined value
 * @param canvas the canvas to zoom
 * @param direction direction of the zoom, -1 to zoom out, +1 to zoom in
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const zoomByStep = (canvas: fabric.Canvas, direction: number): number => {
    if (!canvas) {
        return DEFAULT_ZOOM;
    } // no canvas yet, you shouldn't be calling this yet, but here's a value

    // straight math version, drifts from fixed values (never returns to DEFAULT_ZOOM)
    // return canvas.getZoom() * (1 + Math.sign(direction) * 0.05);

    // TODO: make ZOOM_STEPS global instead of rebuilding each time if we're going to use defined steps
    // make the array of zoom values, starting from dEFAULT_ZOOM and working down to MINIMUM_ZOOM
    // this shouldn't be calculated each time in the real app
    const ZOOM_STEPS = makeZoomStepArray(MINIMUM_ZOOM, DEFAULT_ZOOM, ZOOM_PCT_CHANGE);

    if (direction === 0) {
        return canvas.getZoom();
    }

    const currentZoom = canvas.getZoom();
    const currentIndex = ZOOM_STEPS.findIndex((value) => value >= currentZoom);

    // finds the next value from where you're currently zoomed
    // allows auto-zoom to set an arbitrary value, but then get back to the fixed steps
    const nextZoomLevel = () => {
        if (direction < 0) {
            if (currentIndex === 0 || currentZoom < ZOOM_STEPS[0]) {
                return currentZoom;
            } // we're at or below the minimum, so stay there
            if (currentIndex === -1) {
                return ZOOM_STEPS[ZOOM_STEPS.length - 1];
            } // we're above the max, so go to the max
            return ZOOM_STEPS[currentIndex - 1]; // we're in the array, so step down one
        } else if (direction > 0) {
            if (currentZoom < ZOOM_STEPS[0]) {
                return ZOOM_STEPS[0];
            } // we're below the minimum, so go to the minimum
            if (currentIndex === -1) {
                return currentZoom;
            } // we're at or above the max, so stay there
            if (currentIndex === ZOOM_STEPS.length - 1 && currentZoom <= ZOOM_STEPS[ZOOM_STEPS.length - 1]) {
                return ZOOM_STEPS[ZOOM_STEPS.length - 1];
            } // we're at or just below the max, so go to max
            if (currentZoom < ZOOM_STEPS[currentIndex]) {
                return ZOOM_STEPS[currentIndex];
            } // we're between steps, so bump to the next
            return ZOOM_STEPS[currentIndex + 1]; // we're in the array, so step up one
        }
        return currentZoom;
    };

    return nextZoomLevel();
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const cleanSkeletonPoints = (sk: Skeleton): Skeleton | undefined => {
    let deDupe = sk
        .filter((s, i, a) => {
            if (i === 0) {
                return true;
            }
            if (i === a.length - 1 && MathFunctions.pointsAreEqual(a[0].pos, s.pos)) {
                return false;
            }
            return !(i > 0 && MathFunctions.pointsAreEqual(a[i - 1].pos, s.pos));
        })
        .map((bone) => {
            return { ...bone, pos: new Vec2(bone.pos.x, bone.pos.y) };
        });

    while (deDupe) {
        // started empty or reduced to a single point, it's an invalid skeleton
        if (!deDupe || deDupe.length === 1) {
            return undefined;
        }
        // if < 3 points, no further optimization can be done
        if (deDupe.length < 3) {
            return deDupe;
        }

        const removals: Set<number> = new Set();
        for (let i = 0; i < deDupe.length - 2; i++) {
            const [a, b, c] = [deDupe[i].pos, deDupe[i + 1].pos, deDupe[i + 2].pos];
            // if next three points are all horizontal or vertical and in the same line, remove the middle one
            if (
                ((a.x === b.x && b.x === c.x) || (a.y === b.y && b.y === c.y)) &&
                MathFunctions.lineContainsPoint(new SimpleLine(a, c), b, true, true)
            ) {
                removals.add(i + 1);
            }
        }

        if (removals.size === 0) {
            return deDupe;
        }

        deDupe = deDupe.filter((_value, index) => {
            return !removals.has(index);
        });
    }
    return undefined;
};

export const getOutlinePts = (sk: Skeleton): Array<Partial<CornerInfo>> | undefined => {
    if (!sk || sk.length < 2) {
        return;
    }

    const points = [];

    for (let i = 1; i < sk.length; i++) {
        const startPt = new Vec2(sk[i - 1].pos.x, sk[i - 1].pos.y);
        const endPt = new Vec2(sk[i].pos.x, sk[i].pos.y);

        let U = new Vec2(0, 0);
        let V = new Vec2(0, 0);

        const diffVec = endPt.subtract(startPt, true);
        const normalVec = diffVec.normalize(true).rotate(Math.PI / 2, undefined, true);

        const dPrev = sk[i - 1].depth;

        let angle;

        if (i === 1) {
            U = startPt.add(normalVec.multiply(dPrev, true), true);
            V = startPt.subtract(normalVec.multiply(dPrev, true), true);

            points.push(
                {
                    coords: U,
                    skIndex: i - 1,
                    isUpper: true,
                    angle,
                },
                {
                    coords: V,
                    skIndex: i - 1,
                    isUpper: false,
                    angle,
                }
            );
        }

        if (i === sk.length - 1) {
            U = endPt.add(normalVec.multiply(dPrev, true), true);
            V = endPt.subtract(normalVec.multiply(dPrev, true), true);
        } else {
            const nextPt = new Vec2(sk[i + 1].pos.x, sk[i + 1].pos.y);
            const nextDiffVec = nextPt.subtract(endPt, true);

            const dNext = sk[i].depth;
            angle = diffVec.multiply(-1, true).angleTo(nextDiffVec);
            if (angle === undefined || !angle) {
                console.warn('Undefined angle:', sk[i], sk[i + 1], diffVec, nextDiffVec);
            }
            const alpha = Math.atan((dPrev * Math.sin(angle)) / (dNext + dPrev * Math.cos(angle)));

            const dirVec = diffVec.normalize(true).rotate(alpha, undefined, true);
            const length = alpha === 0 ? dPrev : dPrev / Math.sin(alpha);

            U = endPt.add(dirVec.multiply(length, true), true);
            V = endPt.subtract(dirVec.multiply(length, true), true);
        }

        points.splice(
            points.length / 2,
            0,
            {
                coords: U,
                skIndex: i,
                isUpper: true,
                angle,
            },
            {
                coords: V,
                skIndex: i,
                isUpper: false,
                angle,
            }
        );
    }

    return points;
};

export const getArrowsFromOutlines = (
    pointsForExteriorArrows: Vec2[],
    pointsForInteriorArrows: Vec2[],
    diagonalCorners: { diagonals: boolean[]; cornerIds: (string | undefined)[] },
    matchingEdges: (DrawingEdge | undefined)[]
    // eslint-disable-next-line sonarjs/cognitive-complexity
): ArrowInfo[] => {
    let arrows: ArrowInfo[] = [];

    const newPoints = pointsForExteriorArrows.slice(0);
    newPoints.push(pointsForExteriorArrows[0]);

    for (let i = 1; i < newPoints.length; i++) {
        const startPt = new Vec2(newPoints[i - 1].x, newPoints[i - 1].y);
        const endPt = new Vec2(newPoints[i].x, newPoints[i].y);
        // when a matchingEdge is undefined, it represents a diagonal and should be excluded here
        if (matchingEdges[i - 1] !== undefined && !diagonalCorners.diagonals[i]) {
            const edge: DrawingEdge | undefined = matchingEdges[i - 1];
            if (containsRadiusOut(edge!)) {
                const depth = edge?.radiusSagitta;
                if (depth) {
                    startPt.add(edge?.normalVec.multiply(depth, true), false);
                    endPt.add(edge?.normalVec.multiply(depth, true), false);
                }
            } else if (containsBumpOut(edge!)) {
                const depth = edge?.bumpOut!.depth;
                if (depth) {
                    startPt.add(
                        edge?.normalVec
                            .multiply(depth, true)
                            .add(edge?.normalVec.multiply(DEFAULT_BUMPOUT_DEPTH * 4, true), false)
                    );
                    endPt.add(
                        edge?.normalVec
                            .multiply(depth, true)
                            .add(edge?.normalVec.multiply(DEFAULT_BUMPOUT_DEPTH * 4, true), false)
                    );
                }
            }
        }

        const j = i === newPoints.length - 1 ? 0 : i;
        const arrowShapePoints = getArrowShapeFromSegment(startPt, endPt);
        const length = startPt.subtract(endPt, true).length();
        let diagonalMaximumLength: number | undefined;

        if (diagonalCorners.diagonals[j]) {
            let prevEdgeStart = newPoints[i - 2];
            let prevEdgeEnd = newPoints[i - 1];
            //  when i === 1, one of the adjacent edges with the diagonal corner is the last edge
            if (i === 1) {
                prevEdgeStart = newPoints[newPoints.length - 2];
                prevEdgeEnd = newPoints[newPoints.length - 1];
            }

            const prevStart = new Vec2(prevEdgeStart.x, prevEdgeStart.y);
            const prevEnd = new Vec2(prevEdgeEnd.x, prevEdgeEnd.y);

            const nextStart = new Vec2(newPoints[i].x, newPoints[i].y);
            const nextEnd = new Vec2(newPoints[i + 1].x, newPoints[i + 1].y);
            diagonalMaximumLength = getSmallestAdjacentEdge(
                prevStart.subtract(prevEnd, true).length(),
                nextStart.subtract(nextEnd, true).length()
            );

            diagonalMaximumLength =
                roundToArrowLengthGranularity(diagonalMaximumLength) + roundToArrowLengthGranularity(length);
        }

        arrows.push({
            path: arrowShapePoints,
            isInterior: false,
            size: roundToArrowLengthGranularity(length),
            isDiagonalCorner: diagonalCorners.diagonals[j],
            drawingEdgeId:
                matchingEdges[j] !== undefined && !diagonalCorners.diagonals[i] ? matchingEdges[j]?.id : undefined,
            isVisibleInCanvas: true,
            isActiveForEdgeCalc: true,
            cornerId: diagonalCorners.diagonals[j] ? diagonalCorners.cornerIds[j] : undefined,
            maximumLength: diagonalMaximumLength,
        });
    }

    arrows = arrows.concat(buildInteriorArrows(pointsForInteriorArrows));

    // add interior arrows for radius outs and bump outs
    matchingEdges
        .filter(
            (edge) =>
                (containsRadiusOut(edge!) && edge?.radiusSagitta) || (containsBumpOut(edge!) && edge?.bumpOut?.depth)
        )
        .forEach((edge) => {
            let arrowStartPt: Vec2;
            if (containsRadiusOut(edge!)) {
                arrowStartPt = MathFunctions.getMidpoint(edge!.startDisplayVec, edge!.endDisplayVec);
            } else {
                arrowStartPt = edge!.startDisplayVec
                    .add(edge!.dirVec.normalize(true).multiply(edge!.bumpOut!.clockwiseRemainderLength, true), true)
                    .add(edge!.dirVec.normalize(true).multiply(edge!.bumpOut!.length / 2, true), true);
            }
            const intArrowLen = containsRadiusOut(edge!) ? edge?.radiusSagitta : edge?.bumpOut?.depth;
            const arrowEndPt = arrowStartPt.add(edge!.normalVec.multiply(intArrowLen!, true), true);
            const arrowShapePoints = getArrowShapeFromSegment(arrowStartPt, arrowEndPt, true);
            arrows.push({
                path: arrowShapePoints,
                isInterior: true,
                size: roundToArrowLengthGranularity(intArrowLen!),
                isVisibleInCanvas: true,
                isActiveForEdgeCalc: true,
                drawingEdgeId: edge?.id,
                isRadiusOut: containsRadiusOut(edge!),
                isBumpOut: containsBumpOut(edge!),
            });
        });

    arrows = orderBy(arrows, ['isDiagonalCorner'], ['asc']);

    return arrows;
};

export const getRectangleSideItemArrowsFromOutlines = (points: Vec2[], label?: string): ArrowInfo[] => {
    const arrows = [];

    const newPoints = points.slice(0);
    newPoints.push(points[0]);

    const ltPoint = 0;
    const rtPoint = 1;
    const rbPoint = 2;
    const lbPoint = 3;
    for (let i = 1; i < newPoints.length; i++) {
        const startPt = newPoints[i - 1];
        const endPt = newPoints[i];

        if (startPt !== newPoints[rbPoint] && endPt !== newPoints[lbPoint]) {
            let backsplashLetter = '';
            const arrowShapePoints = getArrowShapeFromSegment(startPt, endPt);

            if (startPt === newPoints[ltPoint] && endPt === newPoints[rtPoint]) {
                backsplashLetter = label ?? '';
            }
            arrows.push({ path: arrowShapePoints, label: backsplashLetter });
        }
    }
    return arrows;
};

export const getBumpOutArrowsFromOutlines = (points: Vec2[]): ArrowInfo[] => {
    const arrows = [];

    const newPoints = points.slice(0);
    newPoints.push(points[0]);

    for (let i = 1; i < newPoints.length; i += 2) {
        const arrowShapePoints = getArrowShapeFromSegment(newPoints[i - 1], newPoints[i]);
        arrows.push({ path: arrowShapePoints, label: '' });
    }
    return arrows;
};

const buildInteriorArrows = (points: Vec2[]): ArrowInfo[] => {
    const arrows: ArrowInfo[] = [];

    const length = points.length;

    if (length <= 6) {
        return [];
    }

    for (let i = 1; i < length / 2 - 2; i++) {
        let depth = DEFAULT_DEPTH; //default depth;
        const firstEdge = points[i + 1].subtract(points[i], true);

        const normalVec = firstEdge.normalize(true).rotate(Math.PI / 2);
        const diaVec = points[i + 1].subtract(points[points.length - i - 2], true);

        depth = Math.abs(normalVec.x * diaVec.x + normalVec.y * diaVec.y);

        const midPt = points[i + 1].add(points[i], true).multiply(0.5);

        const secondPt = midPt.subtract(normalVec.multiply(depth, true), true);

        const interiorSegment = [midPt, secondPt];

        const arrowShapePoints = getArrowShapeFromSegment(interiorSegment[0], interiorSegment[1], true);
        const length = interiorSegment[0].subtract(interiorSegment[1], true).length();
        arrows.push({
            path: arrowShapePoints,
            isInterior: true,
            size: roundToArrowLengthGranularity(length),
            isVisibleInCanvas: true,
            isActiveForEdgeCalc: true,
            interiorSkIndex: i,
        });
    }

    return arrows;
};

const getArrowShapeFromSegment = (startPt: Vec2, endPt: Vec2, isInterior?: boolean): Vec2[] => {
    const arrowPoints = [];
    const diffVec = endPt.subtract(startPt, true).normalize();

    const normalVec = diffVec.rotate(Math.PI / 2, undefined, true);

    const offset = isInterior ? 0 : OFFSET_MEASUREMENT;
    const U = startPt.add(normalVec.multiply(offset, true), true);
    const V = endPt.add(normalVec.multiply(offset, true), true);

    arrowPoints.push(U, V);

    let topArrowPt = U.add(diffVec.rotate(Math.PI / 4, undefined, true).multiply(NORMALIZE_ARROW), true);
    let bottomArrowPt = U.add(diffVec.rotate(-Math.PI / 4, undefined, true).multiply(NORMALIZE_ARROW), true);

    arrowPoints.push(topArrowPt, U, bottomArrowPt);

    topArrowPt = V.add(diffVec.rotate((Math.PI * 3) / 4, undefined, true).multiply(NORMALIZE_ARROW), true);
    bottomArrowPt = V.add(diffVec.rotate((-Math.PI * 3) / 4, undefined, true).multiply(NORMALIZE_ARROW), true);

    arrowPoints.push(topArrowPt, V, bottomArrowPt);

    return arrowPoints;
};

export const getDirection = (line: LineSegment): string => {
    const diffVec = line.endPoint.subtract(line.startPoint, true);

    let direction = LineDirection.TOP;

    if (Math.abs(diffVec.y) < Math.abs(diffVec.x) * 0.0875) {
        //Above condition is for checking which direction current edge is laid towards, more likely towards the xAxis or yAxis
        //With this information, we will put measurement text horizontally or vertically
        // In other words, the case for angle between the diff vector and x axis is less than 5'
        if (diffVec.x > 0) {
            direction = LineDirection.BOTTOM;
        } else {
            direction = LineDirection.TOP;
        }
    } else if (diffVec.y < 0) {
        if (Math.abs(diffVec.x) < COORDS_EPSILON) {
            direction = LineDirection.VERTICAL_RIGHT;
        } else {
            direction = LineDirection.RIGHT;
        }
    } else if (Math.abs(diffVec.x) < COORDS_EPSILON) {
        direction = LineDirection.VERTICAL_LEFT;
    } else {
        direction = LineDirection.LEFT;
    }

    return direction;
};

/**
 * func getHighlightPoints
 * To get expanded outlines for highlights from existing outline of a drawing shape
 * so that it won't overlap eachother.
 *
 * Steps for this algorithm:
 * 1. For each vertex i in existing outline, take nested 2 points like i-1, i+1, and
 *  construct incoming vector {i, i-1} and outcoming vector {i, i+1}
 * 2. With these 2 vectors, get sum vector of them and this will be the direction where
 *  current vertex will be expanded.
 * 3. Use a certain expanding factor and position current vertex, this will be the highlight point.
 */
export const getHighlightPoints = (dShape: DrawingShape): Vec2[] => {
    const highlightPoints = [];

    let beforeVec = new Vec2(0, 0);
    let afterVec = new Vec2(0, 0);
    let dirVec = new Vec2(0, 0);

    const outlines = getOutlinePointsFromShape(dShape);

    for (let i = 0; i < outlines.length; i++) {
        if (i === 0) {
            beforeVec = outlines[i].subtract(outlines[outlines.length - 1], true);
            afterVec = outlines[i].subtract(outlines[i + 1], true);
        } else if (i === outlines.length - 1) {
            beforeVec = outlines[i].subtract(outlines[i - 1], true);
            afterVec = outlines[i].subtract(outlines[0], true);
        } else {
            beforeVec = outlines[i].subtract(outlines[i - 1], true);
            afterVec = outlines[i].subtract(outlines[i + 1], true);
        }

        beforeVec.normalize();
        afterVec.normalize();

        dirVec = beforeVec.add(afterVec, true).normalize();

        const normalVec = beforeVec.rotate(Math.PI / 2, undefined, true);

        if (normalVec.x * dirVec.x + normalVec.y * dirVec.y < 0) {
            dirVec.multiply(-1);
        }

        const newPt = outlines[i].add(dirVec.multiply(OFFSET_HIGHLIGHT_BORDER), true);

        highlightPoints.push(newPt);
    }

    return highlightPoints;
};
export const getCornerLabelPosition = (
    beforePos: Vec2,
    currentPos: Vec2,
    afterPos: Vec2,
    textW: number,
    textH: number
): Vec2 => {
    let pos = new Vec2(0, 0);

    // use before and after vectors of the current vector to get the sum of the 2 vectors
    // this sum will give the direction  of the corner measurement

    const beforeVec = currentPos.subtract(beforePos, true).normalize();
    const afterVec = currentPos.subtract(afterPos, true).normalize();

    const dirVec = beforeVec.add(afterVec, true).normalize();

    const normalVec = beforeVec.rotate(Math.PI / 2, undefined, true);

    if (normalVec.x * dirVec.x + normalVec.y * dirVec.y < 0) {
        dirVec.multiply(-1);
    }

    const diffVec = dirVec.multiply(OFFSET_MEASUREMENT_TEXT, true);
    pos = currentPos.add(diffVec, true);

    const partName = getQuarterPart(dirVec);

    // shift the corner measurement dependeing in which quarters the initial point is
    switch (partName) {
        case QUARTERPART_NAME.POS_X_AXIS:
            pos.set(pos.x, pos.y - textH * 1.5, false);
            break;
        case QUARTERPART_NAME.NEG_X_AXIS:
            pos.set(pos.x - textW, pos.y - textH * 1.5, false);
            break;
        case QUARTERPART_NAME.POS_Y_AXIS:
            pos.set(pos.x - textW, pos.y, false);
            break;
        case QUARTERPART_NAME.NEG_Y_AXIS:
            pos.set(pos.x - textW, pos.y - textH, false);
            break;
        case QUARTERPART_NAME.QAURTER_1:
            pos.set(pos.x + textW * 0.25, pos.y - textH * 0.66, false);
            break;
        case QUARTERPART_NAME.QAURTER_2:
            pos.set(pos.x + textW * 0.25, pos.y, false);
            break;
        case QUARTERPART_NAME.QAURTER_3:
            pos.set(pos.x - textW * 0.75, pos.y, false);
            break;
        case QUARTERPART_NAME.QAURTER_4:
            pos.set(pos.x - textW * 0.75, pos.y - textH * 0.66, false);
            break;
        default:
            break;
    }
    return pos;
};

export const getQuarterPart = (vec: Vec2): string => {
    let partName = '';
    const { x, y } = vec;

    if (Math.abs(y) < Math.abs(x) * 0.0875) {
        if (x >= 0) {
            partName = QUARTERPART_NAME.POS_X_AXIS;
        } else if (x < 0) {
            partName = QUARTERPART_NAME.NEG_X_AXIS;
        }
    } else if (Math.abs(x) < Math.abs(y) * 0.0875) {
        if (y >= 0) {
            partName = QUARTERPART_NAME.POS_Y_AXIS;
        } else if (y < 0) {
            partName = QUARTERPART_NAME.NEG_Y_AXIS;
        }
    } else if (x > 0 && y < 0) {
        partName = QUARTERPART_NAME.QAURTER_1;
    } else if (x > 0 && y > 0) {
        partName = QUARTERPART_NAME.QAURTER_2;
    } else if (x < 0 && y > 0) {
        partName = QUARTERPART_NAME.QAURTER_3;
    } else {
        partName = QUARTERPART_NAME.QAURTER_4;
    }

    return partName;
};

export const getOutlinePointsFromShape = (dShape: DrawingShape): Array<Vec2> => {
    if (!dShape?.cornerMap) {
        return [];
    }
    return Object.values(dShape.cornerMap).map((corner) => corner.coords);
};

export const addCustomEdgeVirtualCorner = (cornerIn: Vec2 | undefined, edge: DrawingEdge): Vec2 | undefined => {
    let cornerOut;
    const addDepth = containsRadiusOut(edge) ? edge.radiusSagitta : edge.bumpOut?.depth;

    if (addDepth != null) {
        cornerOut = cornerIn!.add(edge.normalVec.multiply(addDepth, true), true);
    }
    return cornerOut;
};

export const getBigArcPoints = (edge: DrawingEdge, usePrevEdge: boolean): Vec2[] => {
    const bigArcPoints: Vec2[] = [];

    if (containsRadiusOut(edge)) {
        const changeDir = usePrevEdge ? 1 : -1;
        const chordLength = MathFunctions.getDistanceBetween(edge.startDisplayVec, edge.endDisplayVec);
        const bigArc = edge.radiusSagitta! > chordLength / 2;
        if (bigArc) {
            const chordMidPoint = MathFunctions.getMidpoint(edge.startDisplayVec, edge.endDisplayVec);
            const radius = MathFunctions.calculateRadiusFromArc(chordLength, edge.radiusSagitta!);
            bigArcPoints[0] = chordMidPoint.add(edge.dirVec.normalize(true).multiply(radius * changeDir, true), true);
            bigArcPoints[1] = bigArcPoints[0].add(edge.normalVec.multiply(edge.radiusSagitta!, true), true);
        }
    }
    return bigArcPoints;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getOutlinePointsFromModifiedShape = (dShape: DrawingShape, forArrows?: boolean): Array<Vec2> => {
    if (!dShape?.cornerMap) {
        return [];
    }
    const outline = [];

    // remove the next 3 lines
    const dummyVec = new Vec2(0, 0);
    outline.push(dummyVec);
    outline.pop();

    if (forArrows) {
        // if edge before this point is a radius out, add a point using normVec on that edge (unless this is an end replacement point, then don't)
        // if edge after this point is a radius out, add a point using normVec on that edge (unless this is a start replacement point, then don't)
        // if neither of the above is true, just add the regular point, or replacement points as
        Object.values(dShape.cornerMap).map((corner) => {
            corner.diagonalLength && corner.isVisibleInCanvas
                ? outline.push(corner.replacementStartCoords, corner.replacementEndCoords)
                : outline.push(corner.coords);
        });
    } else {
        Object.values(dShape.cornerMap).map((corner) => {
            const prevEdge: DrawingEdge | undefined = dShape.drawingEdges.find(
                (edge) => edge.endCornerId === corner.id
            );
            if (prevEdge && prevEdge.customSideType !== undefined) {
                outline.push(
                    addCustomEdgeVirtualCorner(
                        (corner.diagonalLength || corner.size! > 0.75) && corner.isVisibleInCanvas
                            ? corner.replacementStartCoords
                            : corner.coords,
                        prevEdge
                    )
                );
                const bigArcPoints = getBigArcPoints(prevEdge, true);
                if (bigArcPoints.length) {
                    outline.push(bigArcPoints[1], bigArcPoints[0]);
                }
            }

            (corner.diagonalLength || corner.size! > 0.75) && corner.isVisibleInCanvas && corner.angle! < 0
                ? outline.push(corner.replacementStartCoords, corner.replacementEndCoords)
                : outline.push(corner.coords);

            const nextEdge: DrawingEdge | undefined = dShape.drawingEdges.find(
                (edge) => edge.startCornerId === corner.id
            );
            if (nextEdge && nextEdge.customSideType !== undefined) {
                const bigArcPoints = getBigArcPoints(nextEdge, false);
                if (bigArcPoints.length) {
                    outline.push(bigArcPoints[0], bigArcPoints[1]);
                }
                outline.push(
                    addCustomEdgeVirtualCorner(
                        (corner.diagonalLength || corner.size! > 0.75) && corner.isVisibleInCanvas
                            ? corner.replacementEndCoords
                            : corner.coords,
                        nextEdge
                    )
                );
            }
        });
    }

    return outline;
};

export const getOutlineDiagonalFlagsFromModifiedShape = (
    dShape: DrawingShape
): { diagonals: boolean[]; cornerIds: (string | undefined)[] } => {
    if (!dShape?.cornerMap) {
        return { diagonals: [], cornerIds: [] };
    }

    const diagonals: boolean[] = [];
    const cornerIds: (string | undefined)[] = [];
    // @TODO
    // This should be refactored! I spent almost three days trying to understand what's happening here and how the
    // generated data is used in other places.
    // The push(false, true) should be changed with some real information... corner ids or something
    // related to the diagonal corners.
    // The diagonals should be directly mapped to a corner instead of adding (true, false) when a diagonal corner is
    // found.
    Object.values(dShape.cornerMap).map((corner) => {
        corner.diagonalLength && corner.isVisibleInCanvas ? diagonals.push(false, true) : diagonals.push(false);
        corner.diagonalLength && corner.isVisibleInCanvas
            ? cornerIds.push(corner.id, corner.id)
            : cornerIds.push(undefined);
    });

    return { diagonals: diagonals, cornerIds: cornerIds };
};

export const getOutlineEdges = (dShape: DrawingShape): Array<DrawingEdge | undefined> => {
    if (!dShape?.cornerMap) {
        return [];
    }

    const outlineEdges: (DrawingEdge | undefined)[] = [];
    Object.values(dShape.cornerMap).map((corner) => {
        const prevEdge: DrawingEdge | undefined = dShape.drawingEdges.find((edge) => edge.startCornerId === corner.id);
        const customSide = prevEdge?.customSideType !== undefined ? prevEdge : undefined;
        corner.diagonalLength && corner.isVisibleInCanvas
            ? outlineEdges.push(undefined, customSide)
            : outlineEdges.push(customSide);
    });
    return outlineEdges;
};

export const getRefinedEdgePoints = ([startX, startY, endX, endY]: Array<number>, stroke?: number) => {
    const offset = stroke ? stroke * 0.5 : 0;
    return [startX - offset, startY - offset, endX - offset, endY - offset];
};

export const makeNewPoint = (points: Array<Vec2>, defaultW: number): ((idx: number, pt: Vec2) => Vec2 | null) => {
    /**
     * This module restrict the free drawing to keep vertical or horizontal direction
     * If the offest between previous and current points is bigger than the given width,
     * then we change the direction. Or we keep the direction.
     * Also, when we change the direction, we add extra point as a middle point.
     */
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useCallback(
        (idx: number, pt: Vec2) => {
            let newPt = null;
            if (!points[idx]) {
                return newPt;
            }
            if (Math.abs(points[idx].x - pt.x) >= defaultW) {
                //Horizontal
                newPt = new Vec2(pt.x, points[idx].y);
            } else if (Math.abs(points[idx].y - pt.y) >= defaultW) {
                //Vertical
                newPt = new Vec2(points[idx].x, pt.y);
            } else if (points.length < 2 || (points.length === 2 && idx === 0)) {
                if (Math.abs(points[0].x - pt.x) > Math.abs(points[0].y - pt.y)) {
                    // Horizontal to Vertical
                    newPt = new Vec2(pt.x, points[0].y);
                } else {
                    // Vertical to Horizontal
                    newPt = new Vec2(points[0].x, pt.y);
                }
            }
            return newPt;
        },
        [points]
    );
};

export const freeDrawerRealTimeLength = (points: Array<Vec2>): { length: number; pos: Vec2 } => {
    let length = 0.0;
    let dirVec = new Vec2(0, 0);
    for (let i = 1; i < points.length; i++) {
        const diff = points[i].subtract(points[i - 1], true);
        const d = diff.length();
        length += d;
        if (i === points.length - 1) {
            dirVec = diff.normalize(true);
        }
    }

    const roundedValue = roundToArrowLengthGranularity(length);
    const newPos = MathFunctions.addVectors(
        points[points.length - 1],
        MathFunctions.multiplyVectorByScalar(dirVec, FREEDRAWER_REALTIMELENGTH_OFFSET)
    );
    return { length: roundedValue, pos: newPos };
};

export const generateSkeletonData = (points: Array<Vec2>, depth: number): Skeleton => {
    return points.map((pt: Vec2) => {
        return {
            pos: new Vec2(pt.x, pt.y),
            depth,
        } as SkeletonSeg;
    });
};

// Get upper, bottom, left, right direction vector for given vector
export const isCorrectDirection = (vec: Vec2, direction: string): boolean => {
    let result = true;
    let dirVec = new Vec2(1, 0);
    switch (direction) {
        case DIRECTION.UP:
            dirVec = new Vec2(0, -1);
            break;
        case DIRECTION.DOWN:
            dirVec = new Vec2(0, 1);
            break;
        case DIRECTION.LEFT:
            dirVec = new Vec2(-1, 0);
            break;
        case DIRECTION.RIGHT:
            dirVec = new Vec2(1, 0);
            break;
    }

    if (vec.x * dirVec.x + vec.y * dirVec.y <= 0) {
        result = false;
    }

    return result;
};

/**
 * Checks whether @param testPoint is inside @param polygon
 * or if it's part of its edges
 */
export const isPointInsidePolygon = (polygon: Vec2[], testPoint: Vec2): boolean => {
    const targetLines = MathFunctions.getLinesFromPolygonPoints(polygon);
    const sourceLine = new SimpleLine(testPoint, new Vec2(testPoint.x, TEN_THOUSAND));

    const isPointPartOfThePolygon = targetLines?.some((targetLine) =>
        new Vec2(testPoint)?.isPointOnLine(targetLine.point1, targetLine.point2)
    );

    const intersectionCount = targetLines?.reduce(
        (numberOfIntersections, targetLine) =>
            numberOfIntersections + (MathFunctions.linesIntersect(sourceLine, targetLine) ? 1 : 0),
        0
    );

    // if an even number of intersections are found, the point is not inside the polygon
    return !(intersectionCount % 2 === 0) || isPointPartOfThePolygon;
};

const getSmallestAdjacentEdge = (firstEdgeDimension: number, secondEdgeDimension: number): number => {
    // returning the smallest adjacent edge
    // 1 is subtracted from the edge dimension because of the minimum edge dimension constraint
    if (firstEdgeDimension < secondEdgeDimension) {
        return Math.sqrt(2) * (firstEdgeDimension - 1);
    } else {
        return Math.sqrt(2) * (secondEdgeDimension - 1);
    }
};
